/**
 * TODO - preamble
 */
import * as React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import {
    FormSelectChoice,
    VerificationService,
    InactiveMilitaryPersonalInfoViewModel,
    FieldValidationErrors,
    MilitaryStatusResponse,
    Organization,
    InactiveMilitaryPersonalInfoResponse,
    ExtendedFieldValidationErrors,
    Metadata,
} from '../../lib/types/types';
import { logger } from '../../lib/utils/logger/logger';
import { MilitaryStatusComponent as MilitaryStatus } from '../FormFields/MilitaryStatus/MilitaryStatusComponent';
import { BranchOfServiceComponent as BranchOfService } from '../FormFields/BranchOfService/BranchOfServiceComponent';
import { FirstNameComponent as FirstName } from '../FormFields/FirstName/FirstNameComponent';
import { LastNameComponent as LastName } from '../FormFields/LastName/LastNameComponent';
import { BirthDateComponent as BirthDate } from '../FormFields/BirthDate/BirthDateComponent';
import { EmailComponent as Email } from '../FormFields/Email/EmailComponent';
import { FormFooterComponent as FormFooter } from '../FormFooter/FormFooterComponent';
import { DischargeDateComponent as DischargeDate } from '../FormFields/DischargeDate/DischargeDateComponent';
import { VerificationStepsEnum, MilitaryStatusDefaultMessagesEnum } from '../../lib/types/runtimeTypes';
import { isFormFilled } from '../../lib/validators/validators';
import { HowDoesVerifyingWorkComponent as HowDoesVerifyingWork } from '../HowDoesVerifyingWork/HowDoesVerifyingWorkComponent';
import { hasFailedInstantMatch } from '../../lib/VerificationService/VerificationServiceHelpers';
import { updateFieldValidationErrorsByFieldId, updateViewModelOrganization, handleEmailOnKeyDown, submitForm } from '../../lib/utils/stepComponentHelpers/stepComponentHelpers';
import { setRef, resetRefs } from '../../lib/refs/refs';
import { LogoComponent } from '../LogoComponent/LogoComponent';
import { MarketConsentWrapperComponent } from '../FormFields/MarketConsentWrapper/MarketConsentWrapperComponent';
import { PhoneNumberComponent as PhoneNumber } from "../FormFields/PhoneNumber/PhoneNumberComponent";


interface StepInactiveMilitaryPersonalInfoComponentProps {
    intl: any;
    verificationService: VerificationService;
}

export const StepInactiveMilitaryPersonalInfo = ({ intl, verificationService }: StepInactiveMilitaryPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as InactiveMilitaryPersonalInfoViewModel;
    const previousViewModel = verificationService.previousViewModel as InactiveMilitaryPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as InactiveMilitaryPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

    const updateInactiveMilitaryViewModel = (key: keyof InactiveMilitaryPersonalInfoViewModel, value: any) => {
        const nextState: InactiveMilitaryPersonalInfoViewModel = produce(viewModel, (draft: InactiveMilitaryPersonalInfoViewModel) => {
            draft[key] = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const submitStep = () => {
        const nextState = produce(viewModel, () => {
        });
        logger.info('StepMilitaryStatusComponent submitting form');
        verificationService.submitStep(
            VerificationStepsEnum.collectMilitaryStatus,
            nextState,
            verificationService.previousVerificationResponse || verificationService.verificationResponse,
        );
    };

    const getAvailableMilitaryStatuses = ():FormSelectChoice[] => {
        const availableStatusesResponse =
            (verificationService.verificationResponse as MilitaryStatusResponse).availableStatuses ||
            (verificationService.previousVerificationResponse as MilitaryStatusResponse).availableStatuses;
        const availableStatuses: FormSelectChoice[] = [];

        for (const status of availableStatusesResponse) {
            availableStatuses.push({
                value: status,
                label: intl.formatMessage({ id: status, defaultMessage: MilitaryStatusDefaultMessagesEnum[status] }),
            });
        }
        return availableStatuses;
    };

    if ((viewModel.status && previousViewModel && previousViewModel.status !== viewModel.status) ||
        (viewModel.status && verificationService.verificationResponse.currentStep === VerificationStepsEnum.collectMilitaryStatus)) {
        submitStep();
    }

    // Refs should be reset to ensure the field order isn't affected by the previous step
    resetRefs();

    return (
        <div id="sid-step-inactive-military-personal-info" className="sid-l-container">
            { failedInstantMatch ?
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name & Birthdate" />
                    </div>

                    <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="
                                Enter your first and last name on the form as it appears on your military document(s).
                                Keep in mind that may be your given name."
                        />
                    </div>
                </div>
            :
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Military-Only Offer" />
                    </div>

                    <div className="sid-header__subtitle sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.subtitle"
                            defaultMessage="Verify you're a member of the armed forces." />
                        &nbsp;
                        <HowDoesVerifyingWork verificationService={verificationService} />
                    </div>
                </div>
            }

            <MilitaryStatus
                value={{
                    value: (verificationService.viewModel as InactiveMilitaryPersonalInfoViewModel).status,
                    label: MilitaryStatusDefaultMessagesEnum[(verificationService.viewModel as InactiveMilitaryPersonalInfoViewModel).status],
                }}
                isErrored={!!fieldValidationErrors.status}
                options={ getAvailableMilitaryStatuses() }
                onChange={
                    (status: FormSelectChoice) => {
                        updateInactiveMilitaryViewModel('status', status ? (status.value as string) : '');
                        updateFieldValidationErrorsByFieldId('status', status ? (status.value as string) : '', verificationService);
                    }
                }
            />

            <BranchOfService
                autoFocus={viewModel.status && viewModel.organization.id === 0 ? true : false}
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
                value={(verificationService.viewModel as InactiveMilitaryPersonalInfoViewModel).organization}
                verificationService={verificationService}
                organizations={verificationService.orgList}
            />

            <div className="sid-names">
                <FirstName

                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={
                        (newValue) => {
                            updateInactiveMilitaryViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastName

                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={
                        (newValue) => {
                            updateInactiveMilitaryViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                onChange={(newValue) => {
                    updateInactiveMilitaryViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <DischargeDate
                isErrored={!!fieldValidationErrors.dischargeDate}
                onChange={(newValue) => {
                    updateInactiveMilitaryViewModel('dischargeDate', newValue);
                    updateFieldValidationErrorsByFieldId('dischargeDate', newValue, verificationService);
                }}
                value={viewModel.dischargeDate}
            />

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Personal email address is recommended"
                    />
                }
                onChange={
                    (newValue) => {
                        updateInactiveMilitaryViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={event => handleEmailOnKeyDown(event)}
            />
            {
                verificationService.programTheme.isSmsNotifierConfigured ?
                    <PhoneNumber
                        value={viewModel.phoneNumber}
                        isErrored={!!fieldValidationErrors.phoneNumber}
                        onChange={
                            (newValue) => {
                                updateInactiveMilitaryViewModel('phoneNumber', newValue);
                                updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                            }
                        }
                    />
                    : null
            }
            <MarketConsentWrapperComponent
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateInactiveMilitaryViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectInactiveMilitaryPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        ref={button => setRef('submitButton', button)}
                    >
                        { failedInstantMatch ?
                            <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                        :
                            <FormattedHTMLMessage id="verifyMyMilitaryStatus" defaultMessage="Verify My Military Status" />
                        }
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="militaryInfoShared"
                    defaultMessage={'Information entered here will be used for verification purposes.'}
                />
            </FormFooter>
        </div>
    );
};

// TODO: TypeScript complains about verificationService
// StepInactiveMilitaryPersonalInfo.propTypes = {
//     verificationService: VerificationServiceShape.isRequired,
// };

export const StepInactiveMilitaryPersonalInfoComponent = injectIntl(StepInactiveMilitaryPersonalInfo);
